import { useEffect } from "react";

const BUILD_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const RELOAD_TARGET_STORAGE_KEY = "deploy-refresh-target";
const RELOAD_QUERY_PARAM = "__refresh";

const normalizeAssetPath = (assetPath) => {
  if (!assetPath) return null;

  try {
    return new URL(assetPath, window.location.origin).pathname;
  } catch (error) {
    return null;
  }
};

const getManifestUrl = () => {
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}/asset-manifest.json?ts=${Date.now()}`;
};

const getCurrentMainAssetPath = () => {
  const currentMainScript = Array.from(document.scripts).find((script) =>
    /\/static\/js\/main\.[^/]+\.js($|\?)/.test(script.src || "")
  );

  return normalizeAssetPath(currentMainScript?.src);
};

const getLatestMainAssetPath = async () => {
  const response = await fetch(getManifestUrl(), { cache: "no-store" });

  if (!response.ok) {
    return null;
  }

  const manifest = await response.json();
  return normalizeAssetPath(manifest?.files?.["main.js"]);
};

const cleanupRefreshQueryParam = () => {
  const currentUrl = new URL(window.location.href);

  if (!currentUrl.searchParams.has(RELOAD_QUERY_PARAM)) {
    return;
  }

  currentUrl.searchParams.delete(RELOAD_QUERY_PARAM);
  const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  window.history.replaceState({}, document.title, nextUrl);
};

const reloadWithCacheBust = (targetAssetPath) => {
  sessionStorage.setItem(RELOAD_TARGET_STORAGE_KEY, targetAssetPath);

  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set(RELOAD_QUERY_PARAM, Date.now().toString());
  window.location.replace(currentUrl.toString());
};

export const useDeployRefresh = () => {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      return undefined;
    }

    let isCancelled = false;

    const checkForDeployUpdate = async () => {
      if (document.visibilityState === "hidden") {
        return;
      }

      try {
        const [currentMainAssetPath, latestMainAssetPath] = await Promise.all([
          Promise.resolve(getCurrentMainAssetPath()),
          getLatestMainAssetPath(),
        ]);

        if (isCancelled || !currentMainAssetPath || !latestMainAssetPath) {
          return;
        }

        if (currentMainAssetPath !== latestMainAssetPath) {
          const pendingReloadTarget = sessionStorage.getItem(RELOAD_TARGET_STORAGE_KEY);

          if (pendingReloadTarget === latestMainAssetPath) {
            return;
          }

          reloadWithCacheBust(latestMainAssetPath);
          return;
        }

        if (sessionStorage.getItem(RELOAD_TARGET_STORAGE_KEY) === currentMainAssetPath) {
          sessionStorage.removeItem(RELOAD_TARGET_STORAGE_KEY);
        }

        cleanupRefreshQueryParam();
      } catch (error) {
        // Ignore intermittent network/cache errors and try again later.
      }
    };

    checkForDeployUpdate();

    const intervalId = window.setInterval(checkForDeployUpdate, BUILD_CHECK_INTERVAL_MS);
    const handleFocus = () => checkForDeployUpdate();
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkForDeployUpdate();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);
};
